import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  DollarSign, Users, Mail, Zap, TrendingUp, Clock, Package as PackageIcon,
  BarChart3, Loader2, ArrowLeft, Eraser, FileText,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("sb_admin_token") || "";
const authHeaders = () => ({ Authorization: `Bearer ${getToken()}` });

const money = (n) => `$${(n || 0).toLocaleString("en-US")}`;

const RANGE_OPTIONS = [
  { value: 30,  label: "30d" },
  { value: 90,  label: "90d" },
  { value: 180, label: "6mo" },
  { value: 365, label: "1y" },
];

const CHART_COLORS = ["#00f0ff", "#a78bfa", "#ff0055", "#22c55e", "#f59e0b", "#f97316", "#3b82f6", "#ec4899"];

const Card = ({ title, value, sub, icon: Icon, accent = "#00f0ff", testId }) => (
  <div
    data-testid={testId}
    className="relative rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-md p-5 md:p-6 hover:border-white/20 transition-colors"
  >
    <div className="flex items-start justify-between mb-3">
      <div className="text-[10px] uppercase tracking-[0.28em] text-white/50">{title}</div>
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center border"
        style={{ background: `${accent}12`, borderColor: `${accent}30`, color: accent }}
      >
        <Icon size={16} />
      </div>
    </div>
    <div className="font-display font-bold text-2xl md:text-3xl tracking-tight">{value}</div>
    {sub && <div className="text-[#a395c3] text-xs mt-1">{sub}</div>}
  </div>
);

const Panel = ({ title, right, children, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6"
  >
    <div className="flex items-center justify-between mb-4">
      <div className="font-display font-bold text-lg">{title}</div>
      {right}
    </div>
    {children}
  </div>
);

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const { data } = await axios.get(`${API}/admin/analytics/overview`, {
          headers: authHeaders(),
          params: { days },
        });
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Failed to load analytics");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [days]);

  const kpis = data?.kpis || {};
  const funnel = data?.funnel || [];
  const utm = data?.utm || [];
  const packages = data?.packages || [];
  const timeseries = data?.timeseries || [];
  const guestTrend = data?.guest_trend_weekly || [];
  const chatConv = data?.chat_conversion || {};
  const respTime = data?.response_time || {};

  // Format ts labels as short "M/D" so the axis doesn't get crowded
  const compactTs = useMemo(
    () => timeseries.map((r) => ({ ...r, label: r.date.slice(5).replace("-", "/") })),
    [timeseries],
  );

  return (
    <div className="relative min-h-screen bg-[#05050a] text-white overflow-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-[#00f0ff]/8 blur-[120px]" />
        <div className="absolute bottom-[-15%] right-[10%] w-[420px] h-[420px] rounded-full bg-[#ff0055]/6 blur-[140px]" />
      </div>

      <div className="relative max-w-[1300px] mx-auto px-5 md:px-10 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <Link to="/admin" data-testid="analytics-back" className="text-white/60 hover:text-white text-sm inline-flex items-center gap-2 mb-3">
              <ArrowLeft size={14} /> Command Center
            </Link>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
              Studio <span className="text-[#00f0ff]">analytics.</span>
            </h1>
            <p className="text-[#a395c3] text-sm mt-2">
              Real-time revenue, funnel + performance — pulled from Mongo, no Monday round-trip.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start md:self-auto flex-wrap" data-testid="analytics-range-picker">
            <Link
              to="/admin/analytics/reports"
              data-testid="analytics-weekly-reports-link"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#00f0ff]/12 border border-[#00f0ff]/40 text-[#00f0ff] text-xs font-semibold hover:bg-[#00f0ff] hover:text-[#05050a] transition"
            >
              <FileText size={11} /> Weekly reports
            </Link>
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDays(opt.value)}
                data-testid={`analytics-range-${opt.value}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                  days === opt.value
                    ? "bg-[#00f0ff] text-[#05050a] border-[#00f0ff]"
                    : "bg-white/[0.05] text-white/70 border-white/15 hover:bg-white/[0.1]"
                }`}
              >
                {opt.label}
              </button>
            ))}
            <ResetAnalyticsButton onReset={() => window.location.reload()} />
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-3 text-white/60" data-testid="analytics-loading">
            <Loader2 size={16} className="animate-spin" /> Loading analytics…
          </div>
        )}
        {err && (
          <div className="rounded-2xl border border-[#ff0055]/30 bg-[#ff0055]/10 p-5 text-sm text-[#ff6b7a]">
            {err}
          </div>
        )}

        {!loading && !err && data && (
          <>
            {/* KPI grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="analytics-kpis">
              <Card testId="kpi-revenue" title="Revenue projected" value={money(kpis.revenue_projected)} sub="Approved + booked × package rate" icon={DollarSign} accent="#22c55e" />
              <Card testId="kpi-bookings" title="Total bookings" value={kpis.total_bookings ?? 0} sub={`${kpis.approval_rate_pct ?? 0}% approval rate`} icon={PackageIcon} accent="#00f0ff" />
              <Card testId="kpi-guests" title="Spotlight guests" value={kpis.total_guests ?? 0} sub={`${kpis.total_contacts ?? 0} contact msgs · ${kpis.total_leads ?? 0} leads`} icon={Users} accent="#a78bfa" />
              <Card testId="kpi-chat" title="Chat → lead" value={`${kpis.chat_conversion_pct ?? 0}%`} sub={`${chatConv.leads_captured || 0} of ${chatConv.sessions || 0} sessions`} icon={Zap} accent="#ff0055" />
            </div>

            {/* Time-series line chart */}
            <Panel title="Activity — last 90 days" testId="panel-timeseries">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={compactTs} margin={{ top: 10, right: 20, left: -10, bottom: 0 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="label" stroke="rgba(255,255,255,0.45)" fontSize={11} tickMargin={8} />
                    <YAxis stroke="rgba(255,255,255,0.45)" fontSize={11} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#11091a", border: "1px solid rgba(0,240,255,0.25)", borderRadius: 12, fontSize: 12 }}
                      cursor={{ stroke: "rgba(0,240,255,0.25)" }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                    <Line type="monotone" dataKey="bookings" stroke="#00f0ff" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="guests" stroke="#a78bfa" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="contacts" stroke="#ff0055" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Panel>

            {/* Funnel + Packages side-by-side */}
            <div className="grid lg:grid-cols-2 gap-6 mt-6">
              <Panel title="Bookings funnel" testId="panel-funnel">
                <div className="space-y-3">
                  {funnel.map((f, i) => {
                    const total = funnel[0]?.count || 1;
                    const pct = Math.round((f.count / total) * 100);
                    return (
                      <div key={f.stage} data-testid={`funnel-${f.stage.toLowerCase()}`}>
                        <div className="flex justify-between text-sm mb-1.5">
                          <span className="text-white/85">{f.stage}</span>
                          <span className="text-white/60 font-mono-tight text-xs">{f.count} · {pct}%</span>
                        </div>
                        <div className="h-2 bg-white/[0.05] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{
                              width: `${pct}%`,
                              background: CHART_COLORS[i % CHART_COLORS.length],
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Panel>

              <Panel title="Package popularity" testId="panel-packages">
                {packages.length === 0 ? (
                  <div className="text-sm text-white/50 py-8 text-center">No bookings yet in range.</div>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={packages} layout="vertical" margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                        <CartesianGrid stroke="rgba(255,255,255,0.06)" horizontal={false} />
                        <XAxis type="number" stroke="rgba(255,255,255,0.45)" fontSize={10} />
                        <YAxis type="category" dataKey="package" stroke="rgba(255,255,255,0.45)" fontSize={10} width={110} />
                        <Tooltip
                          contentStyle={{ background: "#11091a", border: "1px solid rgba(0,240,255,0.25)", borderRadius: 12, fontSize: 12 }}
                          cursor={{ fill: "rgba(0,240,255,0.05)" }}
                        />
                        <Bar dataKey="count" fill="#00f0ff" radius={[0, 6, 6, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Panel>
            </div>

            {/* UTM + Response time + Guest trend */}
            <div className="grid lg:grid-cols-3 gap-6 mt-6">
              <Panel title="Traffic sources" testId="panel-utm">
                {utm.length === 0 ? (
                  <div className="text-sm text-white/50 py-8 text-center">No UTM data yet.</div>
                ) : (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={utm} dataKey="count" nameKey="source" innerRadius={45} outerRadius={80} paddingAngle={3}>
                          {utm.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Legend
                          verticalAlign="bottom"
                          height={40}
                          wrapperStyle={{ fontSize: 10 }}
                          iconSize={8}
                        />
                        <Tooltip
                          contentStyle={{ background: "#11091a", border: "1px solid rgba(0,240,255,0.25)", borderRadius: 12, fontSize: 12 }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Panel>

              <Panel title="Response time (avg)" testId="panel-response">
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="rounded-xl bg-white/[0.04] p-4">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-1.5">Bookings</div>
                    <div className="font-display font-bold text-2xl">
                      {respTime.bookings_avg_hours != null ? `${respTime.bookings_avg_hours}h` : "—"}
                    </div>
                    <div className="text-[#a395c3] text-xs mt-1">to decision</div>
                  </div>
                  <div className="rounded-xl bg-white/[0.04] p-4">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-white/50 mb-1.5">Spotlight</div>
                    <div className="font-display font-bold text-2xl">
                      {respTime.guests_avg_hours != null ? `${respTime.guests_avg_hours}h` : "—"}
                    </div>
                    <div className="text-[#a395c3] text-xs mt-1">to decision</div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10 flex items-center gap-2 text-xs text-white/50">
                  <Clock size={12} /> Lower is better — target &lt; 12h
                </div>
              </Panel>

              <Panel title="Guest trend (13w)" testId="panel-guest-trend">
                {guestTrend.length === 0 ? (
                  <div className="text-sm text-white/50 py-8 text-center">No Spotlight apps yet.</div>
                ) : (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={guestTrend} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                        <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                        <XAxis dataKey="week" stroke="rgba(255,255,255,0.45)" fontSize={9} tick={{ dx: 0 }} interval={1} />
                        <YAxis stroke="rgba(255,255,255,0.45)" fontSize={10} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{ background: "#11091a", border: "1px solid rgba(0,240,255,0.25)", borderRadius: 12, fontSize: 12 }}
                          cursor={{ fill: "rgba(167,139,250,0.08)" }}
                        />
                        <Bar dataKey="count" fill="#a78bfa" radius={[6, 6, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Panel>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ------------------------------- Reset Analytics -------------------------------
const SCOPE_LABELS = {
  chat: { title: "Chat & Sable metrics", body: "Deletes all chat sessions + messages · Zeros Sable analytics + Chat leads" },
  guest_requests: { title: "Guest requests", body: "Deletes every podcast guest submission" },
  bookings: { title: "Bookings", body: "Deletes every studio booking record" },
  leads: { title: "Native leads (CRM)", body: "Deletes rows in the Leads CRM board" },
  hub_events: { title: "Hub activity feed", body: "Live-computed — reset automatically when chat is cleared" },
};

const ResetAnalyticsButton = ({ onReset }) => {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState({ chat: true });
  const [running, setRunning] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const loadPreview = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/analytics/reset/preview`, { headers: authHeaders() });
      setPreview(data);
    } catch (err) {
      // Modal still opens with "—" placeholders. Log for devs though.
      console.warn("[AdminAnalytics] reset preview load failed:", err?.message || err);
    }
  };

  const openModal = () => {
    setConfirmText("");
    setSelected({ chat: true });
    setOpen(true);
    loadPreview();
  };

  const run = async () => {
    const scopes = Object.keys(selected).filter((k) => selected[k]);
    if (scopes.length === 0) return;
    setRunning(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/analytics/reset`,
        { scopes, confirm: true },
        { headers: authHeaders() },
      );
      // toast is set up via sonner already in the app root
      const total = data.grand_total || 0;
      // Use window.alert as a lightweight confirmation since we don't want a toast dep here
      alert(`Reset complete — ${total} document(s) deleted across ${scopes.length} scope(s).`);
      setOpen(false);
      onReset?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "Reset failed");
    } finally {
      setRunning(false);
    }
  };

  const totalSelected = Object.keys(selected)
    .filter((k) => selected[k])
    .reduce((acc, k) => acc + (preview?.[k]?.total || 0), 0);

  return (
    <>
      <button
        onClick={openModal}
        data-testid="analytics-reset-btn"
        className="ml-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border bg-white/[0.03] text-white/60 border-white/10 hover:bg-[#ff0055]/15 hover:border-[#ff0055]/40 hover:text-[#ff5577] transition-colors"
        title="Reset analytics counters"
      >
        <Eraser size={11} /> Reset
      </button>

      {open && (
        <div
          onClick={() => !running && setOpen(false)}
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="analytics-reset-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-2xl bg-[#0b0715] border border-[#ff0055]/25 p-6 relative max-h-[85vh] overflow-y-auto"
          >
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#ff5577] mb-1">Danger zone</div>
            <div className="font-display font-bold text-xl mb-1">Reset analytics counters</div>
            <p className="text-[#a395c3] text-xs mb-4">
              Pick which datasets to hard-delete. This is permanent — a paper trail is written to <code className="text-white/70">reset_audit</code>, but the docs themselves are gone. Preview and production share the same MongoDB.
            </p>

            <ul className="space-y-2 mb-4">
              {Object.entries(SCOPE_LABELS).map(([key, meta]) => {
                const count = preview?.[key]?.total ?? "…";
                const disabled = key === "hub_events";  // no-op today
                return (
                  <li
                    key={key}
                    className={`rounded-xl border p-3 flex items-start gap-3 ${
                      disabled ? "border-white/5 bg-white/[0.01] opacity-50" : "border-white/10 bg-white/[0.02]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      id={`scope-${key}`}
                      checked={!!selected[key]}
                      disabled={disabled}
                      onChange={(e) => setSelected({ ...selected, [key]: e.target.checked })}
                      data-testid={`reset-scope-${key}`}
                      className="mt-1 w-4 h-4 accent-[#ff0055] cursor-pointer disabled:cursor-not-allowed"
                    />
                    <label htmlFor={`scope-${key}`} className="flex-1 min-w-0 cursor-pointer">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className="text-sm font-semibold">{meta.title}</span>
                        <span className="text-[10px] uppercase tracking-[0.2em] text-white/50">
                          {typeof count === "number" ? `${count} docs` : count}
                        </span>
                      </div>
                      <div className="text-[11px] text-white/55">{meta.body}</div>
                    </label>
                  </li>
                );
              })}
            </ul>

            <div className="rounded-xl border border-[#ff0055]/40 bg-[#ff0055]/[0.06] p-3 mb-4">
              <div className="text-[11px] text-[#ff5577] mb-2">
                Type <code className="font-mono font-bold">RESET</code> to arm the delete button.
              </div>
              <input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="RESET"
                data-testid="reset-confirm-input"
                className="w-full px-3 py-2 rounded-lg bg-[#05050a] border border-white/15 text-sm font-mono uppercase focus:outline-none focus:border-[#ff0055]/60"
              />
            </div>

            <div className="flex items-center justify-between gap-3">
              <button
                onClick={() => setOpen(false)}
                disabled={running}
                className="px-4 py-2 rounded-full bg-white/[0.05] border border-white/15 text-xs text-white/70 hover:bg-white/[0.1] disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={run}
                disabled={running || confirmText.trim().toUpperCase() !== "RESET" || totalSelected === 0}
                data-testid="reset-execute-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#ff0055] text-white font-semibold text-xs hover:bg-[#ff2266] disabled:opacity-30 disabled:cursor-not-allowed"
              >
                {running ? <Loader2 size={12} className="animate-spin" /> : <Eraser size={12} />}
                Delete {totalSelected} doc(s)
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
